import assert from 'assert-ts';
import React from 'react';
import {useAsyncCallback} from '../../hooks';
import {Button} from '../button';
import {Layout, LayoutScrollable} from '../layout';
import {Spacer} from '../spacer';
import {Text} from '../text';
import {DialogProps, DialogSize} from './types';

export const Dialog: React.FC<DialogProps> = ({
  title,
  subtitle,
  okButtonDisabled = false,
  size = 'Small',
  okButtonTitle,
  okButtonKind = 'Primary',
  cancelButtonTitle,
  hasCloseButton,
  hideButtons = false,
  onOkButton,
  onCancelButton,
  children,
}) => {
  const [isSubmitting, handleOkButton] = useAsyncCallback(onOkButton);
  if (hasCloseButton) {
    assert(
      onCancelButton,
      'onCancelButton should be set when using hasCloseButton',
    );
  }

  return (
    <Layout
      backgroundColor="background_overlay_dark"
      position="fixed"
      left={0}
      right={0}
      top={0}
      bottom={0}
      centered
      zIndex={100}
      padding="14">
      <Layout width={SizeMap[size].width} minHeight={SizeMap[size].height}>
        <Layout
          raised
          flex
          vertical
          borderRadius="8"
          backgroundColor="background_mono_primary">
          {/** Title */}
          <Layout
            horizontal
            justify="space-between"
            align="center"
            borderColor="background_mono_primary"
            borderBottomColor="border_mono_10"
            borderBottomWidth="1"
            borderBottomStyle={size === 'Small' ? 'none' : 'solid'}
            raised={size !== 'Small'}
            paddingHorizontal="28"
            paddingVertical={size === 'Large' ? '28' : undefined}
            paddingTop={size === 'Small' ? '28' : undefined}>
            <Layout vertical>
              <Text variant="paragraph_20_emphasis">{title}</Text>
              {subtitle && (
                <Text
                  textColor="content_mono_secondary"
                  variant="paragraph_14_default">
                  {subtitle}
                </Text>
              )}
            </Layout>
            {/**Close */}
            {hasCloseButton && onCancelButton && (
              <Button
                leftIcon="close"
                state="Default"
                kind="Clear"
                onPress={onCancelButton}
              />
            )}
          </Layout>
          {/** Content */}
          <Layout flex>
            {size === 'Small' && (
              <Layout
                flex
                horizontal
                paddingVertical="12"
                paddingHorizontal="28">
                {children}
              </Layout>
            )}
            {size === 'Large' && (
              <LayoutScrollable
                vertical
                absolute
                paddingVertical="12"
                paddingHorizontal="28">
                {children}
              </LayoutScrollable>
            )}
          </Layout>
          {/** Buttons */}
          {!hideButtons && (
            <Layout
              justify="flex-end"
              paddingVertical="24"
              paddingHorizontal="28"
              borderColor="background_mono_primary"
              borderTopColor="border_mono_10"
              borderTopStyle={size === 'Small' ? 'none' : 'solid'}
              borderTopWidth="1">
              {onCancelButton && (
                <>
                  <Button
                    kind="Secondary"
                    state={isSubmitting ? 'Disabled' : 'Default'}
                    title={cancelButtonTitle}
                    onPress={onCancelButton}
                  />
                  <Spacer width="8" />
                </>
              )}
              <Button
                kind={okButtonKind}
                state={
                  isSubmitting || okButtonDisabled ? 'Disabled' : 'Default'
                }
                spinning={isSubmitting}
                title={okButtonTitle}
                onPress={handleOkButton}
              />
            </Layout>
          )}
        </Layout>
      </Layout>
    </Layout>
  );
};

const SizeMap: {[key in DialogSize]: {width?: number; height?: number}} = {
  FullScreen: {
    width: undefined,
    height: undefined,
  },
  Large: {
    width: 1004,
    height: 688,
  },
  Small: {
    width: 480,
    height: undefined,
  },
};
